import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from "react";

import { requestAirshowJunctionPreview } from "./airshowJunctionApi";
import {
  createAirshowJunctionPreviewRequest,
  type AirshowJunctionControls,
  type AirshowJunctionPreviewResponse,
} from "./airshowJunctionContract";
import {
  createAirshowJunctionFeedbackEvidence,
  createAirshowJunctionViewEvidence,
  airshowJunctionViewModelId,
  type AirshowJunctionFeedbackEvidence,
  type AirshowJunctionViewerState,
} from "./airshowJunctionFeedback";
import {
  AirshowJunctionViewer,
  type AirshowJunctionCameraPreset,
  type AirshowJunctionLayout,
  type AirshowJunctionShading,
} from "./AirshowJunctionViewer";
import "./airshow-junction.css";

const DEBOUNCE_MS = 180;
const INITIAL_CONTROLS: AirshowJunctionControls = {
  component_a: null,
  component_b: null,
  operation: "smooth-union",
  blend_radius_source_units: 0.16,
  side: "both",
  window_start: 0,
  window_end: 1,
  falloff: 0.1,
  bias: 0,
  profile: "smoothstep",
  grid_resolution: 48,
};

interface JunctionIdentity {
  entryId: string;
  archiveMemberPath: string;
  sourceSha256: string;
  reviewBundleKey: string;
}

interface JunctionLabProps {
  projectId: string;
  identity: JunctionIdentity;
  onEvidenceChange: (evidence: AirshowJunctionFeedbackEvidence | null) => void;
  feedbackPanel: ReactNode;
}

type RequestState = "loading" | "ready" | "unavailable" | "error";
type ResponseState = {
  signature: string;
  request: ReturnType<typeof createAirshowJunctionPreviewRequest>;
  value: AirshowJunctionPreviewResponse;
};

function numberLabel(value: number): string {
  return value.toLocaleString();
}

function matchingJunction(response: AirshowJunctionPreviewResponse | null) {
  const selection = response?.selection;
  if (!selection) return null;
  return response.junctions.find(({ component_a: a, component_b: b }) => (
    (a === selection.component_a && b === selection.component_b)
    || (b === selection.component_a && a === selection.component_b)
  )) ?? null;
}

function junctionRequestSignature(
  identity: JunctionIdentity,
  controls: AirshowJunctionControls,
): string {
  return JSON.stringify([
    identity.entryId, identity.archiveMemberPath, identity.sourceSha256,
    identity.reviewBundleKey, controls.component_a, controls.component_b,
    controls.operation, controls.blend_radius_source_units, controls.side,
    controls.window_start, controls.window_end, controls.falloff, controls.bias,
    controls.profile, controls.grid_resolution,
  ]);
}

export function AirshowJunctionLab({
  projectId,
  identity,
  onEvidenceChange,
  feedbackPanel,
}: JunctionLabProps) {
  const [controls, setControls] = useState<AirshowJunctionControls>(INITIAL_CONTROLS);
  const [responseState, setResponseState] = useState<ResponseState | null>(null);
  const [requestState, setRequestState] = useState<RequestState>("loading");
  const [requestError, setRequestError] = useState<string | null>(null);
  const [layout, setLayout] = useState<AirshowJunctionLayout>("split");
  const [shading, setShading] = useState<AirshowJunctionShading>("solid");
  const [opacity, setOpacity] = useState(0.62);
  const [cameraCommand, setCameraCommand] = useState<{
    id: number;
    preset: AirshowJunctionCameraPreset;
  }>({ id: 0, preset: "isometric" });
  const [viewerState, setViewerState] = useState<AirshowJunctionViewerState | null>(null);
  const requestSequence = useRef(0);
  const evidenceCallback = useRef(onEvidenceChange);
  evidenceCallback.current = onEvidenceChange;
  const currentSignature = junctionRequestSignature(identity, controls);
  const response = responseState?.signature === currentSignature ? responseState.value : null;
  const responseRequest = response ? responseState?.request ?? null : null;
  const viewerModelId = response && responseRequest
    ? airshowJunctionViewModelId(responseRequest, response.preview_bundle_key)
    : `${currentSignature}|pending`;
  const junctionView = useMemo(() => response && responseRequest
    ? createAirshowJunctionViewEvidence({ request: responseRequest, response, viewer: viewerState })
    : null, [response, responseRequest, viewerState]);
  const evidence = useMemo(() => response && junctionView
    ? createAirshowJunctionFeedbackEvidence(response, junctionView) : null,
  [junctionView, response]);
  const updateViewerState = useCallback((next: AirshowJunctionViewerState) => {
    setViewerState(next);
  }, []);
  const clearViewerEvidence = () => {
    setViewerState(null);
    evidenceCallback.current(null);
  };

  useEffect(() => { evidenceCallback.current(evidence); }, [evidence]);

  useEffect(() => {
    setControls(INITIAL_CONTROLS);
    setResponseState(null);
    setRequestState("loading");
    setRequestError(null);
    setViewerState(null);
    evidenceCallback.current(null);
  }, [identity.archiveMemberPath, identity.entryId, identity.reviewBundleKey, identity.sourceSha256]);

  useEffect(() => {
    const sequence = ++requestSequence.current;
    const signature = currentSignature;
    const controller = new AbortController();
    setResponseState(null);
    setRequestState("loading");
    setRequestError(null);
    setViewerState(null);
    evidenceCallback.current(null);
    const timer = window.setTimeout(() => {
      let request;
      try {
        request = createAirshowJunctionPreviewRequest({ identity, controls });
      } catch (error) {
        if (sequence !== requestSequence.current) return;
        setRequestState("error");
        setRequestError(error instanceof Error ? error.message : "Junction controls are invalid");
        return;
      }
      void requestAirshowJunctionPreview({ projectId, request, signal: controller.signal })
        .then((next) => {
          if (controller.signal.aborted || sequence !== requestSequence.current) return;
          setResponseState({ signature, request, value: next });
          setRequestState(next.preview.status === "available" ? "ready" : "unavailable");
        })
        .catch((error: unknown) => {
          if (controller.signal.aborted || sequence !== requestSequence.current
              || (error instanceof Error && error.name === "AbortError")) return;
          setResponseState(null);
          setRequestState("error");
          setRequestError(error instanceof Error ? error.message : "Junction preview is unavailable");
          evidenceCallback.current(null);
        });
    }, DEBOUNCE_MS);
    return () => {
      window.clearTimeout(timer);
      controller.abort();
    };
  }, [
    projectId,
    identity.archiveMemberPath,
    identity.entryId,
    identity.reviewBundleKey,
    identity.sourceSha256,
    controls.bias,
    controls.blend_radius_source_units,
    controls.component_a,
    controls.component_b,
    controls.falloff,
    controls.grid_resolution,
    controls.operation,
    controls.profile,
    controls.side,
    controls.window_end,
    controls.window_start,
  ]);

  const selection = response?.selection;
  const selectedA = controls.component_a ?? selection?.component_a ?? "";
  const selectedB = controls.component_b ?? selection?.component_b ?? "";
  const selectedJunction = matchingJunction(response);
  const components = response?.components ?? [];
  const componentNames = useMemo(() => new Map(
    components.map(({ id, name }) => [id, name]),
  ), [components]);
  const selectedNames = selectedA && selectedB
    ? `${componentNames.get(selectedA) ?? selectedA} ↔ ${componentNames.get(selectedB) ?? selectedB}`
    : "Waiting for a resolved component pair";
  const available = response?.preview.status === "available" ? response.preview : null;
  const unavailable = response?.preview.status === "unavailable" ? response.preview : null;

  const updateControls = (
    updater: (current: AirshowJunctionControls) => AirshowJunctionControls,
  ) => {
    requestSequence.current += 1;
    setResponseState(null);
    setRequestState("loading");
    setRequestError(null);
    setViewerState(null);
    evidenceCallback.current(null);
    setControls(updater);
  };

  const chooseComponent = (field: "component_a" | "component_b", value: string) => {
    if (!value) {
      updateControls((current) => ({ ...current, component_a: null, component_b: null }));
      return;
    }
    const otherField = field === "component_a" ? "component_b" : "component_a";
    const currentOther = (controls[otherField] ?? selection?.[otherField] ?? null);
    const fallback = components.find(({ id }) => id !== value)?.id ?? null;
    updateControls((current) => ({
      ...current,
      [field]: value,
      [otherField]: currentOther && currentOther !== value ? currentOther : fallback,
    }));
  };

  const chooseJunction = (junctionId: string) => {
    const junction = response?.junctions.find(({ id }) => id === junctionId);
    if (!junction) return;
    updateControls((current) => ({
      ...current,
      component_a: junction.component_a,
      component_b: junction.component_b,
    }));
  };

  const setNumber = (
    field: "blend_radius_source_units" | "window_start" | "window_end" | "falloff" | "bias" | "grid_resolution",
    value: number,
  ) => {
    if (Number.isFinite(value)) updateControls((current) => ({ ...current, [field]: value }));
  };

  const viewerReason = unavailable?.message ?? requestError;
  return (
    <div
      className="airshow-review-body airshow-junction-lab"
      data-preview-state={requestState}
      data-request-signature={currentSignature}
      data-testid="openvsp-junction-lab"
    >
      <section className="airshow-review-stage airshow-junction-stage">
        <header className="airshow-junction-banner">
          <div><span>Provisional local tool</span><h2>Diagnostic junction preview</h2></div>
          <strong data-testid="openvsp-junction-fidelity">Semantic adapted diagnostic</strong>
          <p>Not exact, accepted, exportable, or G1/G2 certified.</p>
        </header>
        <div className="airshow-review-toolbar airshow-junction-toolbar">
          <div aria-label="Junction comparison layout" role="group">
            <button aria-pressed={layout === "split"} data-testid="openvsp-junction-layout-split" onClick={() => { clearViewerEvidence(); setLayout("split"); }} type="button">Side by side</button>
            <button aria-pressed={layout === "overlay"} data-testid="openvsp-junction-layout-overlay" onClick={() => { clearViewerEvidence(); setLayout("overlay"); }} type="button">Overlay</button>
          </div>
          <label><span>Shading</span><select data-testid="openvsp-junction-shading" onChange={(event) => { clearViewerEvidence(); setShading(event.currentTarget.value as AirshowJunctionShading); }} value={shading}>
            <option value="solid">Solid</option><option value="wireframe">Wireframe</option><option value="normals">Surface normals</option>
          </select></label>
          <label className="airshow-junction-opacity"><span>Symmetric opacity</span><input data-testid="openvsp-junction-opacity" max="1" min="0.1" onChange={(event) => { clearViewerEvidence(); setOpacity(Number(event.currentTarget.value)); }} step="0.05" type="range" value={opacity} /><output>{opacity.toFixed(2)}</output></label>
        </div>
        <AirshowJunctionViewer
          baselineMetrics={available?.baseline ?? null}
          baselineUrl={available?.baseline_url ?? null}
          cameraCommand={cameraCommand}
          layout={layout}
          loading={requestState === "loading"}
          modelId={viewerModelId}
          onViewStateChange={updateViewerState}
          opacity={opacity}
          resultMetrics={available?.result ?? null}
          resultUrl={available?.result_url ?? null}
          shading={shading}
          unavailableReason={viewerReason}
        />
        <footer className="airshow-review-camera airshow-junction-camera">
          <div>{(["front", "side", "top", "isometric"] as const).map((preset) => (
            <button key={preset} onClick={() => { clearViewerEvidence(); setCameraCommand((current) => ({ id: current.id + 1, preset })); }} type="button">{preset}</button>
          ))}</div>
          {requestState === "loading" ? <p data-testid="openvsp-junction-loading">Updating diagnostic preview…</p> : null}
          {requestError ? <p className="airshow-review-alert" data-testid="openvsp-junction-error" role="alert">{requestError}</p> : null}
          {unavailable ? <p className="airshow-review-alert" data-testid="openvsp-junction-unavailable" role="status">{unavailable.message}</p> : null}
          {available?.warnings.map((warning) => <p key={warning}>{warning}</p>)}
        </footer>
      </section>
      <aside className="airshow-review-panel airshow-junction-panel">
        <section className="airshow-junction-truth">
          <h2>Junction controls</h2>
          <p>Source units are unbound. Blend radius is a field-space influence distance, not a CAD radius.</p>
          <strong data-testid="openvsp-junction-selected-names">{selectedNames}</strong>
        </section>
        <section className="airshow-junction-controls">
          <label><span>Junction candidate</span><select data-testid="openvsp-junction-select" onChange={(event) => chooseJunction(event.currentTarget.value)} value={selectedJunction?.id ?? ""}>
            <option value="">Auto-select component pair</option>
            {response?.junctions.map(({ id, label }) => <option key={id} value={id}>{label}</option>)}
          </select></label>
          <p data-testid="openvsp-junction-candidate-evidence">Candidate evidence: {selectedJunction?.evidence ?? "coarse-field-proximity"}. This is not an exact intersection loop.</p>
          <div className="airshow-junction-pair">
            <label><span>Component A</span><select data-testid="openvsp-junction-component-a" onChange={(event) => chooseComponent("component_a", event.currentTarget.value)} value={selectedA}>
              <option value="">Auto</option>{components.map(({ id, name }) => <option key={id} value={id}>{name}</option>)}
            </select></label>
            <label><span>Component B</span><select data-testid="openvsp-junction-component-b" onChange={(event) => chooseComponent("component_b", event.currentTarget.value)} value={selectedB}>
              <option value="">Auto</option>{components.map(({ id, name }) => <option key={id} value={id}>{name}</option>)}
            </select></label>
          </div>
          <label><span>Operation</span><select data-testid="openvsp-junction-operation" onChange={(event) => {
            const operation = event.currentTarget.value as AirshowJunctionControls["operation"];
            updateControls((current) => ({ ...current, operation }));
          }} value={controls.operation}>
            <option value="hard-union">Hard join</option><option value="smooth-union">Soft blend</option><option value="fillet-proxy">Fillet proxy (field)</option><option value="aero-fairing">Aero fairing</option>
          </select></label>
          <label><span>Side</span><select data-testid="openvsp-junction-side" onChange={(event) => {
            const side = event.currentTarget.value as AirshowJunctionControls["side"];
            updateControls((current) => ({ ...current, side }));
          }} value={controls.side}>
            <option value="both">Both (±Y)</option><option value="positive-y">Starboard (+Y)</option><option value="negative-y">Port (−Y)</option>
          </select></label>
          <div className="airshow-junction-numbers">
            <label><span>Radius · source units</span><input data-testid="openvsp-junction-radius" max="1000000" min="0" onChange={(event) => setNumber("blend_radius_source_units", Number(event.currentTarget.value))} step="0.01" type="number" value={controls.blend_radius_source_units} /></label>
            <label><span>Window start</span><input data-testid="openvsp-junction-window-start" max="1" min="0" onChange={(event) => setNumber("window_start", Number(event.currentTarget.value))} step="0.01" type="number" value={controls.window_start} /></label>
            <label><span>Window end</span><input data-testid="openvsp-junction-window-end" max="1" min="0" onChange={(event) => setNumber("window_end", Number(event.currentTarget.value))} step="0.01" type="number" value={controls.window_end} /></label>
            <label><span>Falloff</span><input data-testid="openvsp-junction-falloff" max="0.5" min="0" onChange={(event) => setNumber("falloff", Number(event.currentTarget.value))} step="0.01" type="number" value={controls.falloff} /></label>
            <label><span>Blend bias</span><input data-testid="openvsp-junction-bias" max="1" min="-1" onChange={(event) => setNumber("bias", Number(event.currentTarget.value))} step="0.05" title="Negative shifts the field blend toward component B; positive shifts it toward component A" type="number" value={controls.bias} /></label>
            <label><span>Grid resolution</span><input data-testid="openvsp-junction-grid-resolution" max="64" min="24" onChange={(event) => setNumber("grid_resolution", Number(event.currentTarget.value))} step="1" type="number" value={controls.grid_resolution} /></label>
          </div>
          <label><span>Profile</span><select data-testid="openvsp-junction-profile" onChange={(event) => {
            const profile = event.currentTarget.value as AirshowJunctionControls["profile"];
            updateControls((current) => ({ ...current, profile }));
          }} value={controls.profile}>
            <option value="constant">Constant</option><option value="smoothstep">Smoothstep</option><option value="bell">Bell</option>
          </select></label>
        </section>
        <section className="airshow-junction-metrics">
          <h2>Diagnostic mesh</h2>
          <dl>
            <div><dt>Baseline</dt><dd data-testid="openvsp-junction-counts-baseline">{available ? `${numberLabel(available.baseline.vertices)} vertices · ${numberLabel(available.baseline.triangles)} triangles` : "Unavailable"}</dd></div>
            <div><dt>Result</dt><dd data-testid="openvsp-junction-counts-result">{available ? `${numberLabel(available.result.vertices)} vertices · ${numberLabel(available.result.triangles)} triangles` : "Unavailable"}</dd></div>
            <div><dt>Continuity</dt><dd>Not certified</dd></div>
            <div><dt>Export</dt><dd>Blocked</dd></div>
          </dl>
        </section>
        {feedbackPanel}
      </aside>
    </div>
  );
}
